import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ROUTES } from '../../common-files/enums/server-routes';

// Super Admin API calls. Access is via the grants-all short-circuit (backend) +
// the guard bypass (frontend); no per-view permissions involved.
@Injectable({ providedIn: 'root' })
export class SuperAdminService {
  constructor(private http: HttpClient) {}

  // -- users --
  listUsers(): Observable<any> {
    return this.http.get(ROUTES.SA_USERS);
  }

  createUser(payload: any): Observable<any> {
    return this.http.post(ROUTES.SA_USERS, payload);
  }


  setUserRestricted(sumId: number, restricted: boolean): Observable<any> {
    const action = restricted ? 'restrict' : 'unrestrict';
    return this.http.post(`${ROUTES.SA_USERS}/${sumId}/${action}`, {});
  }

  listUserRoles(sumId: number): Observable<any> {
    return this.http.get(`${ROUTES.SA_USERS}/${sumId}/roles`);
  }

  assignRole(sumId: number, payload: any): Observable<any> {
    return this.http.post(`${ROUTES.SA_USERS}/${sumId}/roles`, payload);
  }

  revokeRole(sumId: number, suremId: number): Observable<any> {
    return this.http.delete(`${ROUTES.SA_USERS}/${sumId}/roles/${suremId}`);
  }

  // -- roles --
  // user access: roles + permissions grouped Place -> Role -> View -> Permission
  getUserAccess(sumId: number): Observable<any> {
    return this.http.get(`${ROUTES.SA_USERS}/${sumId}/access`);
  }

  setUserRoleStatus(sumId: number, suremId: number, active: boolean): Observable<any> {
    return this.http.patch(`${ROUTES.SA_USERS}/${sumId}/roles/${suremId}/status`, { active });
  }

  grantUserPermission(sumId: number, suremId: number, svpId: number): Observable<any> {
    return this.http.post(`${ROUTES.SA_USERS}/${sumId}/permissions`, { suremId, svpId });
  }

  setUserPermissionStatus(sumId: number, supdId: number, active: boolean): Observable<any> {
    return this.http.patch(`${ROUTES.SA_USERS}/${sumId}/permissions/${supdId}/status`, { active });
  }

  setUserPermissionsStatus(sumId: number, supdIds: number[], active: boolean): Observable<any> {
    return this.http.patch(`${ROUTES.SA_USERS}/${sumId}/permissions/status`, { supdIds, active });
  }

  listRoles(): Observable<any> {
    return this.http.get(ROUTES.SA_ROLES);
  }

  createRole(payload: any): Observable<any> {
    return this.http.post(ROUTES.SA_ROLES, payload);
  }

  updateRole(roleId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.SA_ROLES}/${roleId}`, payload);
  }

  getRoleViews(roleId: number): Observable<any> {
    return this.http.get(`${ROUTES.SA_ROLES}/${roleId}/views`);
  }

  setRoleViews(roleId: number, svpIds: number[]): Observable<any> {
    return this.http.put(`${ROUTES.SA_ROLES}/${roleId}/views`, { svpIds });
  }

  // catalogue of view-permission pairs assignable to a role
  listViewPermissions(): Observable<any> {
    return this.http.get(ROUTES.SA_VIEW_PERMISSIONS);
  }

  // -- establishments (branches) --
  listEstablishments(): Observable<any> {
    return this.http.get(ROUTES.SA_ESTABLISHMENTS);
  }

  createEstablishment(payload: any): Observable<any> {
    return this.http.post(ROUTES.SA_ESTABLISHMENTS, payload);
  }

  updateEstablishment(establishmentId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.SA_ESTABLISHMENTS}/${establishmentId}`, payload);
  }

  // -- views --
  listViews(): Observable<any> {
    return this.http.get(ROUTES.SA_VIEWS);
  }

  createView(payload: any): Observable<any> {
    return this.http.post(ROUTES.SA_VIEWS, payload);
  }

  updateView(viewId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.SA_VIEWS}/${viewId}`, payload);
  }

  // view <-> permission pairs
  getViewPermissionPairs(viewId: number): Observable<any> {
    return this.http.get(`${ROUTES.SA_VIEWS}/${viewId}/permissions`);
  }

  attachPermission(viewId: number, permissionId: number): Observable<any> {
    return this.http.post(`${ROUTES.SA_VIEWS}/${viewId}/permissions`, { permissionId });
  }

  detachPermission(viewId: number, svpId: number): Observable<any> {
    return this.http.delete(`${ROUTES.SA_VIEWS}/${viewId}/permissions/${svpId}`);
  }

  // -- permissions --
  listPermissions(): Observable<any> {
    return this.http.get(ROUTES.SA_PERMISSIONS);
  }

  createPermission(payload: any): Observable<any> {
    return this.http.post(ROUTES.SA_PERMISSIONS, payload);
  }

  updatePermission(permissionId: number, payload: any): Observable<any> {
    return this.http.put(`${ROUTES.SA_PERMISSIONS}/${permissionId}`, payload);
  }

  // -- view as role (preview) --
  viewAsRole(roleId: number): Observable<any> {
    return this.http.get(`${ROUTES.SA_VIEW_AS_ROLE}/${roleId}`);
  }
}
